import { RefObject, useRef } from 'react'
import styled from 'styled-components'

import {
  Button,
  Carat,
  RotateClockwise,
  SelectMark,
  fromMd,
  untilMd,
} from '@syconium/little-miss-figgy'

import { NextDialogProvider } from '../../../app/_components/dialogs/NextDialog.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'

import * as Styles from './styles'

export type EmbroideryModalShellProps = {
  isOpen: boolean
  onClose(): void
  title: string
  /** Lets the flow move focus to the title, e.g. when it swaps views. */
  titleRef: RefObject<HTMLHeadingElement | null>
  /** Goes back one view. The flow owns where "back" leads, and passing nothing hides the control. */
  onBack?: () => void
  /** The garment image and anything drawn over it. */
  preview: React.ReactNode
  /** The options side panel. */
  children: React.ReactNode
}

export const EmbroideryModalShell: React.FC<EmbroideryModalShellProps> = ({
  isOpen,
  onClose,
  title,
  titleRef,
  onBack,
  preview,
  children,
}) => {
  const {
    embroidery: {
      ariaLabelCloseModal,
      close: closeText,
      modal: { backButton: backText, previewTitle },
    },
  } = useTranslation()

  const closeButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <NextDialogProvider open={isOpen}>
      <Styles.Dialog
        analyticsTitle='Embroidery'
        variant='default'
        backdrop={Styles.Backdrop}
        backdropTiming={Styles.BACKDROP_TIMING}
        dialogTiming={Styles.DIALOG_TIMING}
        onClose={onClose}
        aria-label={previewTitle}
        content={<Styles.DialogBody />}
        render={props => (
          <Styles.DialogContainer>
            <div {...props} data-testid='embroidery-modal' />
          </Styles.DialogContainer>
        )}
      >
        <Styles.DialogBody>
          <Styles.Layout>
            <Styles.Half>
              <Styles.Header>
                <Styles.HeaderSide>
                  {onBack !== undefined && (
                    <BackButton
                      aria-label={backText}
                      borderless
                      variant='ghost'
                      size='small'
                      onClick={onBack}
                    >
                      <Carat
                        rotateClockwise={RotateClockwise.Half}
                        height='16px'
                        width='16px'
                        strokeWidth='1.1'
                      />
                    </BackButton>
                  )}
                  <DesktopCloseButton
                    variant='black-on-white'
                    onClick={onClose}
                    ref={closeButtonRef}
                    aria-label={ariaLabelCloseModal}
                    autoFocus
                  >
                    <SelectMark /> <CloseButtonText>{closeText}</CloseButtonText>
                  </DesktopCloseButton>
                </Styles.HeaderSide>

                <Styles.HeaderTitleWrapper>
                  <Styles.HeaderTitle ref={titleRef} tabIndex={-1}>
                    {title}
                  </Styles.HeaderTitle>
                </Styles.HeaderTitleWrapper>

                <Styles.HeaderSide>
                  <MobileCloseButton
                    borderless
                    variant='ghost'
                    size='small'
                    onClick={onClose}
                    aria-label={ariaLabelCloseModal}
                  >
                    <SelectMark />
                  </MobileCloseButton>
                </Styles.HeaderSide>
              </Styles.Header>

              <Styles.PreviewWrapper>{preview}</Styles.PreviewWrapper>
            </Styles.Half>

            <Styles.Half>
              <Styles.Panel>{children}</Styles.Panel>
            </Styles.Half>
          </Styles.Layout>
        </Styles.DialogBody>
      </Styles.Dialog>
    </NextDialogProvider>
  )
}

const BackButton = styled(Button)`
  padding-left: ${o => o.theme.spacing(4)};

  ${fromMd} {
    display: none;
  }
`

const MobileCloseButton = styled(Button)`
  background-color: ${o => o.theme.color.fill.background};
  padding-right: ${o => o.theme.spacing(4)};

  svg {
    height: ${o => o.theme.spacing(4)};
    width: ${o => o.theme.spacing(4)};
  }

  path {
    stroke-width: 1;
  }

  ${fromMd} {
    border: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
    padding: ${o => o.theme.spacing(2, 4, 2, 4)};
  }
`

const DesktopCloseButton = styled(MobileCloseButton)`
  ${untilMd} {
    display: none;
  }
`

const CloseButtonText = styled.span`
  padding-left: ${o => o.theme.spacing(2)};
`
